import { useState } from 'react'
import {
    ArrowLeft, Crown, KeyRound, Lock, MicOff, MonitorPlay, RefreshCw, RotateCcw,
    Shield, ShieldMinus, ShieldPlus, Trash2, UserMinus, Users, Video, X
} from 'lucide-react'
import { MriButton, cn } from '@mriqbox/ui-kit'
import type { AdminParticipant, AdminRoom } from '@shared/protocol'
import { Avatar } from '@/components/Avatar'
import { useAdminPanel } from '@/hooks/useAdminPanel'

/**
 * Painel do operador, em `/admin`.
 *
 * O unico link pra ca fica na landing, e so aparece pra quem ja tem o cargo no
 * Discord (`isAdmin` em `/api/me`). Pra todo o resto — inclusive pra quem esta
 * logado mas sem o cargo — a pagina precisa ser indistinguivel de um endereco
 * que nao existe, senao ela se denuncia sozinha.
 */
export function AdminPage() {
    const painel = useAdminPanel()

    // O 404 e definitivo: nao ha o que reconsultar nem o que explicar.
    if (painel.status === 'negado') return <NadaAqui />

    // Sem a primeira resposta ainda. Se a rede estiver falhando, o aviso
    // aparece aqui em vez de a tela ficar num "Carregando..." mudo pra sempre.
    if (!painel.overview) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-2 bg-background text-sm text-muted-foreground">
                {painel.error ? (
                    <>
                        <p role="alert" className="text-destructive">{painel.error}</p>
                        <p className="text-xs">Tentando de novo...</p>
                    </>
                ) : 'Carregando...'}
            </div>
        )
    }

    const salas = painel.overview.rooms
    const pessoas = salas.reduce((total, sala) => total + sala.participants.length, 0)

    return (
        <div className="h-full overflow-y-auto bg-background">
            <div className="mx-auto max-w-4xl px-4 py-6">
                <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border/70 pb-4">
                    <div>
                        <h1 className="flex items-center gap-2 text-lg font-bold text-foreground">
                            <Shield className="h-5 w-5 text-primary" />
                            Painel do operador
                        </h1>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            {salas.length} sala(s) · {pessoas} pessoa(s) conectada(s) · atualiza sozinho
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {/*
                         * A volta pro inicio precisa estar na tela: no app
                         * instalado nao ha barra de endereco nem botao de
                         * voltar, e sem isto o painel virava rua sem saida.
                         */}
                        <MriButton variant="ghost" size="sm" className="gap-2" asChild>
                            <a href="/">
                                <ArrowLeft className="h-3.5 w-3.5" />
                                Voltar
                            </a>
                        </MriButton>
                        <MriButton variant="outline" size="sm" className="gap-2" onClick={painel.refresh}>
                            <RefreshCw className="h-3.5 w-3.5" />
                            Atualizar
                        </MriButton>
                    </div>
                </header>

                {painel.error && (
                    <p role="alert" className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {painel.error}
                    </p>
                )}

                {salas.length === 0 ? (
                    <p className="mt-10 text-center text-sm text-muted-foreground">
                        Nenhuma sala no ar e nenhuma sala fixa cadastrada.
                    </p>
                ) : (
                    <div className="mt-4 space-y-3">
                        {salas.map((sala) => (
                            <RoomCard
                                key={sala.code}
                                sala={sala}
                                onKick={(id) => painel.kick(sala.code, id)}
                                onSetAdmin={(id, admin) => painel.setAdmin(sala.code, id, admin)}
                                onUnban={(id) => painel.unban(sala.code, id)}
                                onDelete={() => void painel.deleteRoom(sala.code)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

/**
 * A mesma cara pra quem errou o endereco e pra quem nao tem o cargo. Um "sem
 * permissao" seria a confirmacao que falta pra quem esta tentando adivinhar.
 */
function NadaAqui() {
    return (
        <div className="flex h-full flex-col items-center justify-center gap-2 bg-background">
            <p className="text-2xl font-bold text-foreground">404</p>
            <p className="text-sm text-muted-foreground">Nada por aqui.</p>
            <a href="/" className="mt-2 text-xs text-primary hover:underline">Voltar ao inicio</a>
        </div>
    )
}

function RoomCard({
    sala,
    onKick,
    onSetAdmin,
    onUnban,
    onDelete
}: {
    sala: AdminRoom
    onKick: (id: string) => void
    onSetAdmin: (id: string, admin: boolean) => void
    onUnban: (id: string) => void
    onDelete: () => void
}) {
    const [confirmando, setConfirmando] = useState(false)

    return (
        <section className="rounded-xl border border-border/70 bg-card/50 p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-sm font-bold tracking-[0.14em] text-foreground">
                            {sala.code}
                        </span>
                        {sala.name && <span className="truncate text-sm text-foreground/90">· {sala.name}</span>}
                        {sala.permanent && (
                            <Tag className="border-primary/30 bg-primary/10 text-primary">fixa</Tag>
                        )}
                        {!sala.live && <Tag className="border-border bg-muted text-muted-foreground">vazia</Tag>}
                        {sala.locked && (
                            <Tag className="border-amber-500/30 bg-amber-500/10 text-amber-400">
                                <Lock className="h-2.5 w-2.5" /> trancada
                            </Tag>
                        )}
                        {sala.requiresPin && (
                            <Tag className="border-amber-500/30 bg-amber-500/10 text-amber-400">
                                <KeyRound className="h-2.5 w-2.5" /> PIN
                            </Tag>
                        )}
                    </div>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {sala.ownerName ? `Dono: ${sala.ownerName} · ` : ''}
                        {sala.participants.length} dentro
                        {sala.lastSeenAt ? ` · visto ${dataCurta(sala.lastSeenAt)}` : ''}
                    </p>
                </div>

                {confirmando ? (
                    <div className="flex items-center gap-1">
                        <MriButton
                            size="sm"
                            variant="destructive"
                            className="h-7 text-[11px]"
                            onClick={() => {
                                onDelete()
                                setConfirmando(false)
                            }}
                        >
                            Apagar mesmo?
                        </MriButton>
                        <button
                            type="button"
                            title="Cancelar"
                            onClick={() => setConfirmando(false)}
                            className="flex h-7 w-7 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        title={sala.live
                            ? 'Encerrar a sala e desconectar quem estiver dentro'
                            : 'Apagar a sala fixa'}
                        onClick={() => setConfirmando(true)}
                        className="flex h-7 items-center gap-1.5 rounded-lg border border-border px-2 text-[11px] text-muted-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                        Apagar
                    </button>
                )}
            </div>

            {sala.participants.length > 0 && (
                <ul className="mt-2 space-y-0.5 border-t border-border/50 pt-2">
                    {sala.participants.map((pessoa) => (
                        <ParticipantRow
                            key={pessoa.id}
                            pessoa={pessoa}
                            onKick={() => onKick(pessoa.id)}
                            onSetAdmin={(admin) => onSetAdmin(pessoa.id, admin)}
                        />
                    ))}
                </ul>
            )}

            {sala.bans.length > 0 && (
                <div className="mt-2 border-t border-border/50 pt-2">
                    <p className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                        <Users className="h-3 w-3" /> Expulsos ({sala.bans.length})
                    </p>
                    <ul className="space-y-0.5">
                        {sala.bans.map((ban) => (
                            <li key={ban.id} className="group flex h-7 items-center gap-2 rounded-md px-1.5 hover:bg-accent/40">
                                <Avatar nickname={ban.nickname} avatar={ban.avatar} size={20} />
                                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                                    {ban.nickname}
                                    <span className="ml-1.5 text-[10px] opacity-70">por {ban.byNickname}</span>
                                </span>
                                <button
                                    type="button"
                                    title={`Liberar ${ban.nickname}`}
                                    onClick={() => onUnban(ban.id)}
                                    className="p-0.5 text-muted-foreground opacity-0 transition-all group-hover:opacity-100 hover:text-emerald-400"
                                >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    )
}

function ParticipantRow({
    pessoa,
    onKick,
    onSetAdmin
}: {
    pessoa: AdminParticipant
    onKick: () => void
    onSetAdmin: (admin: boolean) => void
}) {
    return (
        <li className="group flex h-8 items-center gap-2 rounded-md px-1.5 transition-colors hover:bg-accent/40">
            <Avatar nickname={pessoa.nickname} avatar={pessoa.avatar} size={22} />

            <span className={cn('truncate text-[13px]', pessoa.away ? 'text-muted-foreground/60 italic' : 'text-foreground')}>
                {pessoa.nickname}
            </span>

            {pessoa.host && (
                <span title="Host da sala"><Crown className="h-3 w-3 shrink-0 text-amber-400" /></span>
            )}
            {pessoa.admin && !pessoa.host && (
                <span title="Moderador"><Shield className="h-3 w-3 shrink-0 text-sky-400" /></span>
            )}
            {pessoa.publications.includes('screen') && (
                <span title="Transmitindo a tela"><MonitorPlay className="h-3 w-3 shrink-0 text-destructive" /></span>
            )}
            {pessoa.publications.includes('camera') && (
                <span title="Camera ligada"><Video className="h-3 w-3 shrink-0 text-primary" /></span>
            )}
            {/* Fora da voz o microfone tambem esta fechado — a mesma regra da
                lista de participantes dentro da sala. */}
            {(!pessoa.publications.includes('voice') || pessoa.muted) && (
                <span title="Microfone mudo"><MicOff className="h-3 w-3 shrink-0 text-muted-foreground" /></span>
            )}
            {pessoa.away && (
                <span className="text-[10px] text-muted-foreground/70">reconectando</span>
            )}

            <div className="ml-auto flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                {/* O host da sala nao entra nem sai da lista de moderadores: o
                    cargo dele nao e "admin com outro nome". */}
                {!pessoa.host && (
                    <button
                        type="button"
                        onClick={() => onSetAdmin(!pessoa.admin)}
                        title={pessoa.admin ? `Tirar a moderação de ${pessoa.nickname}` : `Tornar ${pessoa.nickname} moderador`}
                        className={cn(
                            'rounded p-0.5 transition-colors',
                            pessoa.admin ? 'text-sky-400 hover:text-destructive' : 'text-muted-foreground hover:text-sky-400'
                        )}
                    >
                        {pessoa.admin
                            ? <ShieldMinus className="h-3.5 w-3.5" />
                            : <ShieldPlus className="h-3.5 w-3.5" />}
                    </button>
                )}
                <button
                    type="button"
                    onClick={onKick}
                    title={`Remover ${pessoa.nickname} e barrar a volta`}
                    className="rounded p-0.5 text-muted-foreground transition-colors hover:text-destructive"
                >
                    <UserMinus className="h-3.5 w-3.5" />
                </button>
            </div>
        </li>
    )
}

function Tag({ children, className }: { children: React.ReactNode; className?: string }) {
    return (
        <span className={cn(
            'flex items-center gap-0.5 rounded border px-1 py-0.2 text-[9px] font-bold uppercase tracking-wider',
            className
        )}>
            {children}
        </span>
    )
}

function dataCurta(timestamp: number): string {
    return new Date(timestamp).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    })
}
